/**
 * Static history services for public estimate (read-only mode)
 * Returns pre-fetched transaction and listing data from the estimate response
 */

import type { Transaction, Listing } from '@popety-io/types'

interface StaticHistoryServices {
  loadTransaction: (id: number | string) => Promise<Transaction | null>
  loadListing: (id: number) => Promise<Listing | null>
}

const transactionRecords = (data: unknown): Transaction[] =>
  Array.isArray(data)
    ? data.filter((record) => record && typeof record === 'object')
    : []

/**
 * Creates static history services that return data from pre-fetched estimate data
 * @param estimateData - The estimate data containing history information
 */
export const createStaticHistoryServices = (
  estimateData: any
): StaticHistoryServices => {
  // Use the same snapshot as the timeline before saved comparables/legacy data.
  const embeddedTransactions = [
    estimateData?.value?.lands,
    estimateData?.value?.buildings,
  ].flatMap((entities) =>
    Array.isArray(entities)
      ? entities.flatMap((entity) => transactionRecords(entity?.transactions))
      : []
  )
  const transactions: Transaction[] = [
    ...transactionRecords(estimateData?.value_edited?.history?.transactions),
    ...transactionRecords(estimateData?.value_edited?.transactions),
    ...transactionRecords(estimateData?.value?.transactions),
    ...embeddedTransactions,
  ]

  // Get pre-fetched listings from estimate data (both saved and from history)
  const savedListings: Listing[] =
    estimateData?.value_edited?.savedListings || []
  const historyListings: Listing[] =
    estimateData?.value_edited?.historyListings ||
    estimateData?.value?.listings ||
    []
  const allListings = [...savedListings, ...historyListings]

  return {
    /**
     * Load a transaction by ID from pre-fetched data
     */
    loadTransaction: async (
      id: number | string
    ): Promise<Transaction | null> => {
      if (id == null || id === '') return null

      const matches = (candidate: string | number | null | undefined) =>
        candidate != null && String(candidate) === String(id)
      // Several document/step records can share the same source transaction ID.
      const transaction =
        transactions.find((record) => matches(record.id)) ||
        transactions.find((record) => matches(record.transaction_id))
      return transaction || null
    },

    /**
     * Load a listing by ID from pre-fetched data
     */
    loadListing: async (id: number): Promise<Listing | null> => {
      const listing = allListings.find((l) => l.id === id)
      return listing || null
    },
  }
}
